import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { HeartPulse, Camera, User, Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterBraceletPage() {
  const { braceletCode } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [bracelet, setBracelet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    photo_base64: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_email: "",
    emergency_contact_relationship: "",
    allergies: "",
    has_dementia: false,
    additional_notes: ""
  });

  useEffect(() => {
    fetchBracelet();
  }, [braceletCode]);

  const fetchBracelet = async () => {
    try {
      const response = await axios.get(`${API}/bracelet/${braceletCode}`);
      setBracelet(response.data);
      
      if (response.data.is_registered) {
        setError("This bracelet is already registered");
      }
    } catch (err) {
      setError("Bracelet not found");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setFormData({ ...formData, photo_base64: base64 });
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.emergency_contact_name || !formData.emergency_contact_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/profiles`, {
        bracelet_id: bracelet.id,
        ...formData
      });
      
      toast.success("Profile registered successfully!");
      navigate(`/emergency/${braceletCode}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to register profile");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {error}
            </h1>
            {bracelet?.is_registered && (
              <p className="text-slate-600 mb-4">
                This bracelet already has a profile linked to it.
              </p>
            )}
            <Link to={`/emergency/${braceletCode}`}>
              <Button className="bg-teal-600 hover:bg-teal-700">
                View Emergency Info
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-14 w-auto"
            />
          </Link>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Register Bracelet
          </h1>
          <p className="text-slate-600 mt-1">
            Code: <span className="font-mono font-semibold">{braceletCode?.toUpperCase()}</span>
          </p>
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Profile Information</CardTitle>
            <CardDescription>
              Add emergency contact information for this bracelet
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Photo Upload */}
              <div className="text-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  accept="image/*"
                  className="hidden"
                  data-testid="photo-input"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer inline-block"
                >
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Profile preview"
                      className="w-32 h-32 rounded-full object-cover border-4 border-teal-600 mx-auto"
                      data-testid="photo-preview"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-slate-100 rounded-full flex flex-col items-center justify-center border-2 border-dashed border-slate-300 mx-auto hover:border-teal-500 transition-colors">
                      <Camera className="h-8 w-8 text-slate-400 mb-1" />
                      <span className="text-xs text-slate-500">Add Photo</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-2">Optional: Tap to add a photo</p>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12"
                  data-testid="name-input"
                />
              </div>

              {/* Emergency Contact Section */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Emergency Contact
                </h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_name">Contact Name *</Label>
                    <Input
                      id="emergency_contact_name"
                      placeholder="e.g., John Smith"
                      value={formData.emergency_contact_name}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                      required
                      className="h-12"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_phone">Phone Number *</Label>
                    <Input
                      id="emergency_contact_phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                      required
                      className="h-12"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_relationship">Relationship</Label>
                    <Input
                      id="emergency_contact_relationship"
                      placeholder="e.g., Son, Daughter, Caregiver"
                      value={formData.emergency_contact_relationship}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                      className="h-12"
                      data-testid="contact-relationship-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_email">Email (for scan notifications)</Label>
                    <Input
                      id="emergency_contact_email"
                      type="email"
                      placeholder="guardian@email.com"
                      value={formData.emergency_contact_email}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_email: e.target.value })}
                      className="h-12"
                      data-testid="contact-email-input"
                    />
                    <p className="text-xs text-slate-500">Receive email alerts when the bracelet is scanned</p>
                  </div>
                </div>
              </div>

              {/* Medical Info Section */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Medical Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-pink-50 rounded-lg border border-pink-200">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-pink-600" />
                      <div>
                        <Label htmlFor="has_dementia" className="text-pink-800 font-medium">
                          Has Dementia / Memory Issues
                        </Label>
                        <p className="text-sm text-pink-600">Alert responders to be patient</p>
                      </div>
                    </div>
                    <Switch
                      id="has_dementia"
                      checked={formData.has_dementia}
                      onCheckedChange={(checked) => setFormData({ ...formData, has_dementia: checked })}
                      data-testid="dementia-switch"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="allergies">Allergies (keep vague for privacy)</Label>
                    <Textarea
                      id="allergies"
                      placeholder="e.g., Penicillin allergy"
                      value={formData.allergies}
                      onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                      rows={2}
                      data-testid="allergies-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="additional_notes">Additional Notes</Label>
                    <Textarea
                      id="additional_notes"
                      placeholder="Any other important information..."
                      value={formData.additional_notes}
                      onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                      rows={2}
                      data-testid="notes-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-teal-600 hover:bg-teal-700 text-white text-lg font-semibold"
                disabled={submitting}
                data-testid="submit-profile-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Register Bracelet
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">
            Visit stressfreeaging.com
          </a>
        </p>
      </div>
    </div>
  );
}
